import { axiosInstance } from "@/lib/axios-instance";
import { IWorkspace } from "@/types/data-models";
import type { RaiseBoardGlobalApplication } from "@/api/raise-board-api";
import { lookbookAccessHeaders } from "@/lib/lookbook-access";

/**
 * A lookbook as the workspace's own pages see it. `company_ids` rather than
 * application ids: the pipeline and Raiseboard pages key deals by company
 * (DealTransformed.id), so this is what they can actually join against.
 */
export interface Lookbook {
  _id: string;
  name: string;
  public_id: string;
  created_on?: string;
  deal_count: number;
  company_ids: string[];
  /** Whether visitors must enter a password. The password itself never leaves the server. */
  password_protected: boolean;
  /** Whether visitors can upvote and comment without signing in. */
  guest_engagement: boolean;
}

/**
 * A change to who can get in and what they can do. Only the fields present
 * change: a string password sets or replaces it, `null` removes it.
 */
export interface LookbookSettingsInput {
  password?: string | null;
  guest_engagement?: boolean;
}

interface ToggleResponse {
  status: string;
  result: {
    lookbooks_enabled: boolean;
    /** The starter lookbook, present only on the first enable. */
    created: Lookbook | null;
  };
}

interface ListResponse {
  status: string;
  result: { lookbooks: Lookbook[] };
}

interface SingleResponse {
  status: string;
  result: { lookbook: Lookbook };
}

/**
 * The public payload behind /lb/:publicId. Applications carry the lookbook's
 * own upvote_count and comment_count rather than the public board's, so the
 * board and profile components render lookbook-scoped numbers unchanged.
 */
export interface LookbookPublicResponse {
  status: string;
  result: {
    lookbook: {
      _id: string;
      name: string;
      public_id: string;
      password_protected?: boolean;
      guest_engagement?: boolean;
    };
    opportunity: IWorkspace;
    applications: RaiseBoardGlobalApplication[];
  };
}

/**
 * What a lookbook should show for a deal, as the picker sends it.
 *
 * `document_types` is the bulk form: the server resolves each category to that
 * company's newest matching file *at write time*, so a selection made across
 * twelve deals stores twelve concrete allowlists rather than a rule that would
 * quietly publish whatever gets uploaded next.
 */
export interface LookbookVisibilityInput {
  profile: boolean;
  data_room: boolean;
  reports: boolean;
  report_keys: string[];
  /** Single-deal form: exact file ids. */
  document_ids?: string[];
  /** Bulk form: file_type categories, resolved per company by the server. */
  document_types?: string[];
}

/** The picker's options for one company — only what actually exists. */
export interface LookbookShareCompany {
  company_id: string;
  company_name: string;
  documents: Array<{
    _id: string;
    file_name: string;
    file_type: string;
    file_size?: string;
    time_stamp?: string;
    upload_source?: string;
  }>;
  reports: Array<{
    key: string;
    label: string;
    count: number;
    latest_created_on?: string;
  }>;
  /** The config already stored, when editing a deal already in the lookbook. */
  current: {
    profile: boolean;
    data_room: boolean;
    reports: boolean;
    document_ids: string[];
    report_keys: string[];
  } | null;
}

export interface LookbookShareOptions {
  companies: LookbookShareCompany[];
  /** Roll-ups across the selection, for the bulk picker's "in 4 of 7" counts. */
  document_types: Array<{ file_type: string; company_count: number }>;
  reports: Array<{ key: string; label: string; company_count: number }>;
}

export const lookbookApi = {
  toggleEnabled: async (
    opportunityId: string,
    enabled: boolean
  ): Promise<ToggleResponse> => {
    const response = await axiosInstance.post("/toggle_lookbooks", {
      opportunity_id: opportunityId,
      enabled,
    });
    return response.data;
  },

  list: async (opportunityId: string): Promise<Lookbook[]> => {
    const response = await axiosInstance.get<ListResponse>("/lookbooks", {
      params: { opportunity_id: opportunityId },
    });
    return response.data?.result?.lookbooks ?? [];
  },

  create: async (opportunityId: string, name: string): Promise<Lookbook> => {
    const response = await axiosInstance.post<SingleResponse>(
      "/create_lookbook",
      { opportunity_id: opportunityId, name }
    );
    return response.data.result.lookbook;
  },

  rename: async (
    opportunityId: string,
    lookbookId: string,
    name: string
  ): Promise<Lookbook> => {
    const response = await axiosInstance.post<SingleResponse>(
      "/update_lookbook",
      { opportunity_id: opportunityId, lookbook_id: lookbookId, name }
    );
    return response.data.result.lookbook;
  },

  updateSettings: async (
    opportunityId: string,
    lookbookId: string,
    settings: LookbookSettingsInput
  ): Promise<Lookbook> => {
    const response = await axiosInstance.post<SingleResponse>(
      "/update_lookbook_settings",
      { opportunity_id: opportunityId, lookbook_id: lookbookId, ...settings }
    );
    return response.data.result.lookbook;
  },

  /** Trades a lookbook's password for the access token its pages send. */
  unlock: async (publicId: string, password: string): Promise<string> => {
    const response = await axiosInstance.post<{
      status: string;
      result: { token: string };
    }>("/unlock_lookbook", { public_id: publicId, password });
    return response.data.result.token;
  },

  /**
   * Asks a password-protected lookbook's fund for access. Public; the server
   * answers the same whether or not the lookbook exists.
   */
  requestAccess: async (
    publicId: string,
    request: { email: string; name?: string; message?: string }
  ): Promise<void> => {
    await axiosInstance.post("/request_lookbook_access", {
      public_id: publicId,
      ...request,
    });
  },

  /**
   * Shares a lookbook with people by email: each is subscribed to it and sent
   * an invitation with the link. Owner-only on the server.
   */
  invite: async (
    opportunityId: string,
    lookbookId: string,
    emails: string[],
    message?: string
  ): Promise<{ invited: string[]; failed: string[]; invalid: string[] }> => {
    const response = await axiosInstance.post<{
      status: string;
      result: { invited: string[]; failed: string[]; invalid: string[] };
    }>("/invite_to_lookbook", {
      opportunity_id: opportunityId,
      lookbook_id: lookbookId,
      emails,
      message,
    });
    return response.data.result;
  },

  remove: async (
    opportunityId: string,
    lookbookId: string
  ): Promise<{ status: string }> => {
    // Express strips bodies from some DELETE paths inconsistently, but the
    // ownership middleware reads opportunity_id from body, params or query —
    // so it rides in the body here alongside every other management call.
    const response = await axiosInstance.delete(`/lookbook/${lookbookId}`, {
      data: { opportunity_id: opportunityId },
    });
    return response.data;
  },

  setDeals: async (
    opportunityId: string,
    lookbookId: string,
    companyIds: string[],
    member: boolean,
    visibility?: LookbookVisibilityInput
  ): Promise<Lookbook> => {
    const response = await axiosInstance.post<SingleResponse>(
      "/set_lookbook_deals",
      {
        opportunity_id: opportunityId,
        lookbook_id: lookbookId,
        company_ids: companyIds,
        member,
        visibility,
      }
    );
    return response.data.result.lookbook;
  },

  updateDealVisibility: async (
    opportunityId: string,
    lookbookId: string,
    companyIds: string[],
    visibility: LookbookVisibilityInput
  ): Promise<Lookbook> => {
    const response = await axiosInstance.post<SingleResponse>(
      "/update_lookbook_deal_visibility",
      {
        opportunity_id: opportunityId,
        lookbook_id: lookbookId,
        company_ids: companyIds,
        visibility,
      }
    );
    return response.data.result.lookbook;
  },

  getShareOptions: async (
    opportunityId: string,
    companyIds: string[],
    lookbookId?: string
  ): Promise<LookbookShareOptions> => {
    const response = await axiosInstance.get<{
      status: string;
      result: LookbookShareOptions;
    }>("/lookbook_share_options", {
      params: {
        opportunity_id: opportunityId,
        company_ids: companyIds.join(","),
        lookbook_id: lookbookId,
      },
    });
    return (
      response.data?.result ?? {
        companies: [],
        document_types: [],
        reports: [],
      }
    );
  },

  /**
   * One shared report for one deal on a public lookbook page. Anonymous — the
   * lookbook's public_id is the credential, same as the board payload itself.
   */
  getSharedReport: async (
    publicId: string,
    companyId: string,
    reportType: string
  ): Promise<{ report_type: string; items: unknown[] }> => {
    const response = await axiosInstance.get<{
      status: string;
      result: { report_type: string; items: unknown[] };
    }>("/fetch_lookbook_report", {
      params: {
        public_id: publicId,
        company_id: companyId,
        report_type: reportType,
      },
      headers: lookbookAccessHeaders(publicId),
    });
    return response.data.result;
  },

  getByPublicId: async (publicId: string): Promise<LookbookPublicResponse> => {
    const response = await axiosInstance.get<LookbookPublicResponse>(
      "/fetch_lookbook",
      {
        params: { public_id: publicId },
        headers: lookbookAccessHeaders(publicId),
      }
    );
    return response.data;
  },
};
